import { useCallback, useMemo, useState } from 'react'
import { Box, Fab, LinearProgress, Typography, useTheme } from '@mui/material'
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ReferenceArea,
} from 'recharts'
import { useTimelineStyles } from '~/crop/crop-schedule/hooks/use-timeline-styles'
import { MOCK_GDD_SERIES as _MOCK_GDD_SERIES, MOCK_SEASON_PROGRESS } from './mock-gdd-data'
import { Y_AXIS_WIDTH, CHART_MARGIN, doyToDate, formatDoyLabel, formatDoyFull } from './gdd-chart-utils'
import { GddChartTooltip } from './gdd-chart-tooltip'
import { GddChartDecorations } from './gdd-chart-decorations'
import { GddBandFill, GddLines, GddReferenceLines } from './gdd-chart-series'
import { useFeatures } from '~/common/use-features'
import { Refresh } from '@mui/icons-material'
import { TpTooltip } from '~/components'

export const GddChart = ({
  gddBase = '5',
  tempUnit = '°C',
  gdd5Series = null,
  gdd10Series = null,
  lfd = null,
  ffd = null,
  fullscreen = false,
}) => {
  const theme = useTheme()
  const { TimelineStyles } = useTimelineStyles()
  const { features } = useFeatures()

  const isCelsius = tempUnit === '°C'
  const baseLabel = gddBase === '5'
    ? (isCelsius ? '5°C' : '41°F')
    : (isCelsius ? '10°C' : '50°F')

  const currentDoy = useMemo(() => {
    const now = new Date()
    const start = new Date(now.getFullYear(), 0, 0)
    return Math.floor((now - start) / 86400000)
  }, [])

  const activeSeries = gddBase === '5' ? gdd5Series : gdd10Series

  const chartData = useMemo(() => {
    if (!activeSeries?.length) return []
    return activeSeries.map(([doy, avg, std]) => {
      const absStd = Math.abs(std)
      return {
        doy,
        avg: Math.round(avg),
        avgHigh: Math.round(avg + absStd),
        avgLow: Math.round(avg - absStd),
        std: Math.round(absStd),
      }
    })
  }, [activeSeries])

  const ticks = useMemo(() => {
    const seen = new Set()
    return chartData
      .filter(d => {
        const m = doyToDate(d.doy).getUTCMonth()
        if (!seen.has(m)) { seen.add(m); return true }
        return false
      })
      .map(d => d.doy)
  }, [chartData])

  const [xDomain, setXDomain] = useState({ left: 'dataMin', right: 'dataMax' })
  const [refAreaLeft, setRefAreaLeft] = useState(null)
  const [refAreaRight, setRefAreaRight] = useState(null)

  const visibleData = useMemo(() => {
    const leftBound = typeof xDomain.left === 'number'
      ? xDomain.left
      : Math.min(...chartData.map(d => d.doy))
    const rightBound = typeof xDomain.right === 'number'
      ? xDomain.right
      : Math.max(...chartData.map(d => d.doy))

    return chartData.filter(d => d.doy >= leftBound && d.doy <= rightBound)
  }, [chartData, xDomain.left, xDomain.right])

  const maxGdd = useMemo(() =>
    Math.max(...visibleData.map(d => d.avgHigh ?? 0), 0), [visibleData])

  const yTicks = useMemo(() => {
    const max = maxGdd + 100
    const step = max > 2000 ? 500 : max > 1000 ? 250 : 100
    const result = []
    for (let v = 0; v <= max; v += step) result.push(v)
    return result
  }, [maxGdd])
  const yMax = maxGdd + 100

  const eraseFill = fullscreen ? theme.palette.background.default : theme.palette.background.paper

  const onZoomStart = useCallback((e) => {
    if (e?.activeLabel == null) return
    setRefAreaLeft(e.activeLabel)
    setRefAreaRight(undefined)
  }, [])

  const onZoomMove = useCallback((e) => {
    if (refAreaLeft == null || e?.activeLabel == null) return
    setRefAreaRight(e.activeLabel)
  }, [refAreaLeft])

  const onZoomEnd = useCallback(() => {
    if (refAreaLeft == null || refAreaRight == null || refAreaLeft === refAreaRight) {
      setRefAreaLeft(null)
      setRefAreaRight(null)
      return
    }

    let left = Number(refAreaLeft)
    let right = Number(refAreaRight)
    if (Number.isNaN(left) || Number.isNaN(right)) {
      setRefAreaLeft(null)
      setRefAreaRight(null)
      return
    }
    if (left > right) [left, right] = [right, left]

    setXDomain({ left, right })
    setRefAreaLeft(null)
    setRefAreaRight(null)
  }, [refAreaLeft, refAreaRight])

  const resetZoom = useCallback(() => {
    setXDomain({ left: 'dataMin', right: 'dataMax' })
    setRefAreaLeft(null)
    setRefAreaRight(null)
  }, [])

  const isZoomed = xDomain.left !== 'dataMin' || xDomain.right !== 'dataMax'

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isZoomed && (
        <Box sx={{ position: 'relative' }}>
          <TpTooltip title='Reset chart zoom'>
            <Fab
              size="small"
              color="primary"
              onClick={resetZoom}
              sx={{
                position: 'absolute',
                bottom: -2,
                right: 10,
                width: 28,
                height: 28,
                minHeight: 28,
                minWidth: 28,
                boxShadow: 'none',
              }}
            >
              <Refresh
                size='small'
                sx={{ fontSize: 18 }}
              />
            </Fab>
          </TpTooltip>
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {chartData.length === 0 ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="text.disabled">No climate data</Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={CHART_MARGIN}
              onMouseDown={onZoomStart}
              onMouseMove={onZoomMove}
              onMouseUp={onZoomEnd}
              style={{ cursor: 'crosshair' }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={theme.palette.divider}
                vertical={false}
                zIndex={150}
              />
              <GddBandFill eraseFill={eraseFill} />
              <GddChartDecorations
                currentDoy={currentDoy}
                nowLabel={formatDoyFull(currentDoy)}
                lfd={lfd}
                ffd={ffd}
                yMax={yMax}
              />
              <XAxis
                dataKey="doy"
                ticks={ticks}
                tickFormatter={formatDoyLabel}
                tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={{ stroke: theme.palette.divider }}
                type="number"
                allowDataOverflow
                domain={[xDomain.left, xDomain.right]}
              />
              <YAxis
                width={Y_AXIS_WIDTH}
                tick={{ fontSize: 9, fill: theme.palette.text.secondary }}
                tickLine={false}
                axisLine={false}
                allowDataOverflow
                ticks={yTicks}
                domain={[0, yMax]}
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}
                label={{
                  value: `Base ${baseLabel}`,
                  angle: -90,
                  position: 'insideLeft',
                  offset: 10,
                  style: {
                    fontStyle: 'italic',
                    fontSize: 12,
                    fill: theme.palette.text.disabled,
                    textAnchor: 'middle',
                  },
                }}
              />
              <Tooltip
                content={<GddChartTooltip theme={theme} />}
                wrapperStyle={{ zIndex: 10, transition: 'none' }}
                animationDuration={0}
                cursor={{
                  stroke: TimelineStyles.hover.fillColor,
                  strokeWidth: TimelineStyles.hover.width,
                  strokeDasharray: '4 3',
                }}
              />
              <GddLines />
              <Line
                type="monotone"
                dataKey="avg"
                name="avg"
                stroke={theme.palette.text.secondary}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                connectNulls
                isAnimationActive={false}
                legendType="none"
              />
              {refAreaLeft != null && refAreaRight != null && (
                <ReferenceArea
                  x1={refAreaLeft}
                  x2={refAreaRight}
                />
              )}
              <GddReferenceLines currentDoy={currentDoy} lfd={lfd} ffd={ffd} yMax={yMax} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Box>

      {/* Season progress bar — left pad aligns with chart plot area */}
      {features['gdd_current_data'] && (
        <Box
          sx={{
            pl: `${CHART_MARGIN.left}px`,
            pr: `${CHART_MARGIN.right}px`,
            pb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" sx={{ textAlign: 'right', fontSize: '0.65rem', color: 'text.secondary', flexShrink: 0, width: Y_AXIS_WIDTH }}>
            {MOCK_SEASON_PROGRESS}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={MOCK_SEASON_PROGRESS}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              bgcolor: 'action.hover',
              '& .MuiLinearProgress-bar': { borderRadius: 3 },
            }}
          />
        </Box>
      )}
    </Box>
  )
}
